import { getProductVariantKey } from '../../../../../store/helpers/assets/aggregateProductVariants';
import { getListingChannelId } from '../../../../../store/products/helpers/channelIdHelpers';
import resolveSalesChannelId from '../../../../../store/products/helpers/resolveSalesChannelId';

export const UNKNOWN_CHANNEL_ID = 'unknown';

const getListingProductKey = (product) => (
  `${product._id}:${product.isDraft === true ? 'draft' : 'live'}`
);

const getVariantChipKey = (variant) => {
  const base = getProductVariantKey(variant);
  if (variant.type === 'listingVariant') {
    return `${base}:${variant.isDraft === true ? 'draft' : 'live'}`;
  }
  return base;
};

const dedupeByKey = (items, getKey) => {
  const map = new Map();
  items.forEach((item) => {
    const key = getKey(item);
    if (!map.has(key)) map.set(key, item);
  });
  return [...map.values()];
};

const isMasterProductLink = (product, masterProductIds) => {
  if (product.type === 'master') return true;
  if (product.type === 'listing') return false;
  return masterProductIds.has(String(product._id));
};

const isListingProductLink = (product, masterProductIds) => {
  if (product.type === 'listing') return true;
  if (product.type === 'master') return false;
  return !masterProductIds.has(String(product._id));
};

const resolveListingChannelId = (link, salesChannels = []) => {
  const rawChannelId = getListingChannelId(link) || UNKNOWN_CHANNEL_ID;
  if (rawChannelId === UNKNOWN_CHANNEL_ID) return UNKNOWN_CHANNEL_ID;
  return resolveSalesChannelId(rawChannelId, salesChannels);
};

export const groupProductLinks = ({
  products = [],
  productVariants = [],
  masterProducts = [],
  salesChannels = [],
}) => {
  const masterProductIds = new Set(masterProducts.map(({ _id }) => String(_id)));

  const master = { products: [], variants: [] };
  const channelsMap = {};

  const ensureChannel = (channelId) => {
    if (!channelsMap[channelId]) {
      channelsMap[channelId] = {
        channelId,
        products: [],
        variants: [],
      };
    }
    return channelsMap[channelId];
  };

  products.forEach((product) => {
    if (isMasterProductLink(product, masterProductIds)) {
      master.products.push(product);
      return;
    }

    if (isListingProductLink(product, masterProductIds)) {
      ensureChannel(resolveListingChannelId(product, salesChannels)).products.push(product);
    }
  });

  productVariants.forEach((variant) => {
    if (variant.type === 'masterVariant') {
      master.variants.push(variant);
      return;
    }

    if (variant.type === 'listingVariant') {
      ensureChannel(resolveListingChannelId(variant, salesChannels)).variants.push(variant);
    }
  });

  master.products = dedupeByKey(master.products, (product) => String(product._id));
  master.variants = dedupeByKey(master.variants, getVariantChipKey);

  const channels = Object.values(channelsMap)
    .map((channel) => ({
      ...channel,
      products: dedupeByKey(channel.products, getListingProductKey),
      variants: dedupeByKey(channel.variants, getVariantChipKey),
    }))
    .filter((channel) => channel.products.length || channel.variants.length);

  const hasLinks = Boolean(
    master.products.length
    || master.variants.length
    || channels.length,
  );

  return { master, channels, hasLinks };
};
